import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerService from "../../../services/CustomerServices";
import Breadcrumb from "../../../components/Breadcrumb";
import ListCustomer from "../Components/ListCustomer";
import ViewModelCustomer from "../Components/ViewModelCustomer";
import MessageDialog from "../../../components/MessageDialog";

export default function ViewCustomer() {
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("success");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMsgType(type);
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await CustomerService.getAll(currentPage, itemsPerPage, searchTerm);
      const list = Array.isArray(res?.data) ? res.data : [];
      setCustomers(list);
      setPageCount(res?.totalPages || 1);
    } catch (error) {
      console.error("Customer load error:", error);
      showMessage("Failed to load customers", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchTerm]);

  const handleAdd = () => navigate("/cpnl/customers/add");

  const handlePageClick = (event) => {
    const selectedPage = event.selected + 1;
    setCurrentPage(selectedPage);
  };

  const handleModify = () => {
    if (!selected) {
      showMessage("Please select a customer to modify.", "error");
      return;
    }

    if (selected.status !== "Active") {
      showMessage("Inactive customers cannot be modified.", "error");
      return;
    }

    navigate(`/cpnl/customers/add/${selected.id}`);
  };

  const handleView = async () => {
    if (!selected) {
      showMessage("Please select a customer to view.", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await CustomerService.getById(selected.id);
      const data = res?.data?.data || res?.data || res;

      if (!data) {
        showMessage("Failed to load customer details!", "error");
        return;
      }

      setViewData(data);
      setShowViewModal(true);
    } catch (err) {
      console.error(err);
      showMessage("Error loading customer details.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    const customer = customers.find((c) => c.id === id);
    if (!customer) return;

    const confirmMsg =
      customer.status === "Active"
        ? "Are you sure you want to change status to Inactive?"
        : "Are you sure you want to change status to Active?";

    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      await CustomerService.updateStatus(id);
      showMessage(
        customer.status === "Active"
          ? "Customer inactivated successfully!"
          : "Customer activated successfully!",
        "success"
      );
      fetchCustomers();
    } catch (error) {
      console.error(error);
      showMessage("Error updating customer status.", "error");
    } finally {
      setLoading(false);
    }
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewData(null);
  };

  return (
    <>
      <Breadcrumb pageTitle="Customers" secondTitle="Masters"></Breadcrumb>
      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          <MessageDialog
            message={message}
            type={msgType}
            setMessage={setMessage}
            setMsgType={setMsgType}
          />

          <div className="mb-3 pb-2 border-bottom d-flex align-items-end gap-3">
            <div>
              <input
                type="text"
                className="form-control"
                placeholder="Search Customer"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="ms-auto d-flex gap-2">
              <button className="btn btn-success" onClick={handleAdd} disabled={loading}>
                <i className="ti ti-plus me-1"></i> Add
              </button>

              <button className="btn btn-warning" onClick={handleModify} disabled={loading}>
                <i className="ti ti-pencil me-1"></i> Modify
              </button>

              <button className="btn btn-primary" onClick={handleView} disabled={loading}>
                <i className="ti ti-eye me-1"></i> View
              </button>
            </div>
          </div>

          <ListCustomer
            loading={loading}
            filteredCustomers={customers}
            selected={selected}
            setSelected={setSelected}
            handleToggleStatus={handleToggleStatus}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
          />
        </div>
      </div>

      <ViewModelCustomer
        showViewModal={showViewModal}
        viewData={viewData}
        closeViewModal={closeViewModal}
      />
    </>
  );
}
